"use client";
import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
// import { Info } from "lucide-react";
import { LineChart, Line, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { useQuery } from "@tanstack/react-query";

import {
    BULLION,
    BULLION_KEY,
    // BULLION_PRODUCT,
    periodMappings,
    PeriodType,
    TradeData,
} from "@/app/alts/bullion/type-constants";
import { themes } from "@/app/enums/chart-themes";
import { formatIndianCurrency } from "@/app/utils/helper";
import { getBullionPrices } from "@/app/api/service/bullion";
import SkeletonAssetInfo from "@/app/alts/bullion/[type]/[id]/components/skeleton/skeleton-asset-info";
import AssetInformationTitle from "@/app/alts/bullion/[type]/[id]/components/asset-information-title";

interface ChartDataPoint {
    stroke: string;
    strokeWidth: number;
    dataKey: string;
    name: string;
    color: string;
    value: number;
    payload: {
        x: number;
        y: string;
    };
    hide: boolean;
}
interface Tooltips {
    active?: boolean;
    payload?: ChartDataPoint[];
    label?: string;
}
interface Cursor {
    x: number;
    y: number;
}
interface CursorParent {
    points: Cursor[];
}
interface ChartDataPricesPoint {
    x: Date;
    y: number;
}
interface VizProps {
    isMobileWidgetOpen?: boolean;
    product: BULLION_KEY;
    /** When true, title and outer wrapper are rendered by parent (for server title) */
    hideTitle?: boolean;
}

const CustomTooltip = ({ active, payload, label }: Tooltips) => {
    if (active && payload && payload.length) {
        const formattedDate =
            typeof label === "number"
                ? moment(label).format("DD/MM/YYYY")
                : moment(label, "MM/DD/YYYY").format("DD/MM/YYYY");
        return (
            <div className="rounded bg-white p-2 text-sm shadow-md">
                <div className="text-gray-600">
                    {`${formatIndianCurrency(Number(payload[0].value?.toFixed(2)))} | ${formattedDate}`}
                </div>
            </div>
        );
    }
    return null;
};

const CustomCursor = ({ points }: CursorParent) => {
    return (
        <line
            x1={points[0].x}
            y1={0}
            x2={points[0].x}
            y2="100%"
            stroke="#6B7280"
            strokeWidth="1"
            strokeDasharray="3 3"
        />
    );
};

export const getPercentage = (data: TradeData[]) => {
    let percentageChange = 0;
    if (data && data.length > 0) {
        const earliest = data[0]?.buy_pretax || 0;
        const latest = data[data.length - 1]?.buy_pretax || 0;

        if (earliest != 0) {
            percentageChange = Number(
                (((latest - earliest) / earliest) * 100).toFixed(2),
            ); /* (((latest - earliest) / earliest) * 100).toLocaleString("en-IN", {
                maximumFractionDigits: 2,
            }); */
        }
    }
    return percentageChange;
};

const Viz: React.FC<VizProps> = ({ product = BULLION.TYPE as BULLION_KEY, hideTitle = false }) => {
    const [period, setPeriod] = useState<PeriodType>("1Y");
    const [priceDiff, setPriceDiff] = useState<number | null>(null);

    /* const { filteredData, priceDiff, error, refetch } = useBullionGraphData({
        product_code: BULLION_PRODUCT[product].CODE,
        period,
        refetch: false,
    }); */

    const {
        data: filteredData,
        error: error,
        isFetching: isPriceFetching,
    } = useQuery({
        queryKey: [period],
        queryFn: () =>
            getBullionPrices({
                product_code: BULLION[product].CODE,
                quantity: "10",
                time_frame: period,
                unit_type: "A",
            }),
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        // enabled: false,
    });
    console.log("filteredData", filteredData);

    const [data, setData] = useState<ChartDataPricesPoint[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        try {
            if (filteredData?.data && filteredData?.data?.prices.length > 0) {
                const filteredDataPrices = filteredData?.data.prices.map(
                    (item: {
                        date_time: string | Date;
                        buy_pretax: number;
                    }): ChartDataPricesPoint => ({
                        x: new Date(item.date_time),
                        y: item.buy_pretax,
                    }),
                );
                setData(filteredDataPrices);
                setPriceDiff(getPercentage(filteredData?.data.prices));
            } else {
                setPriceDiff(null);
            }
        } catch (ex) {
            setPriceDiff(null);
            console.error("Error in processing filtered data:", ex);
        }
    }, [filteredData]);

    const formatXAxis = (timestamp: number) => {
        return moment(timestamp).format("DD/MM/YY");
    };

    const handlePeriodChange = (newPeriod: PeriodType) => {
        /* if (setTimePeriod) {
            setTimePeriod(newPeriod || "1_month");
        } */
        // fnSetProductPrice();
        setPeriod(newPeriod);
        // refetch();
    };

    const getLabelForKey = (key: string): string | null => {
        const found = periodMappings.find((item) => item.key === key);
        return found ? found.label : null;
    };

    // data logger only for development
    /* useEffect(() => {
        console.log("- Product filteredData:", data, isPriceFetching);
    }, [filteredData]); */

    if (error) return <div>Error loading data</div>;

    const chartContent = data?.length ? (
        <>
            <div className="flex justify-between">
                <div className="align-items-center mb-8 flex sm:mb-4">
                    <div className="flex items-center">
                        <span
                            className={`detail padding-x-20 my-auto flex items-center pb-0 text-xl ${priceDiff && priceDiff < 0 ? "text-brand-primary-500" : "text-emerald-700"} `}
                        >
                            {priceDiff !== null
                                ? Number(priceDiff) > 0
                                    ? "+" + priceDiff + "%"
                                    : priceDiff + "%" /* (${perDiff}%) */
                                : "--"}
                            <span className="ml-[7px] flex items-center text-lg text-neutral-300">{` ${getLabelForKey(period)}`}</span>
                        </span>
                    </div>
                    {/* <div className="relative z-10 ml-[7px] flex items-center">
                                <button className="group relative">
                                    <Info
                                        width="16"
                                        height="16"
                                        className={`${isMobileWidgetOpen ? "text-transparent" : "text-neutral-300"} cursor-pointer`}
                                    />
                                    <div className="I absolute left-0 top-full -ml-s10 mt-2 hidden w-60 rounded-md bg-black p-4 text-xs text-white group-hover:block sm:mx-auto md:-ml-s15 md:w-80">
                                        The historical prices shown on the InCred Money platform
                                        have been arrived at by using a combination of internal and
                                        external sources which reflect the demand and supply
                                        situation that is further based on factors like Financial
                                        performance, IPO related news, etc. These prices are
                                        indicative in nature. We do not recommend investment
                                        decisions taken solely based on the historical prices
                                        mentioned on our platform.
                                    </div>
                                </button>
                            </div> */}
                </div>
            </div>

            <section ref={containerRef} className="viz-section-width">
                <div className="relative h-[150px] w-full md:h-[320px]">
                    {isPriceFetching ? (
                        <div className="absolute z-10 flex h-full w-full items-center justify-center">
                            Loading...
                        </div>
                    ) : !filteredData || !filteredData?.data?.prices ? (
                        <div className="absolute z-10 flex h-full w-full items-center justify-center">
                            Error loading data
                        </div>
                    ) : (
                        <ResponsiveContainer /* width="100%" height="100%" */>
                            <LineChart
                                data={data}
                                margin={{ top: 20, right: 15, bottom: 5, left: 15 }}
                            >
                                <XAxis dataKey="x" tickFormatter={formatXAxis} hide={true} />
                                <Tooltip
                                    content={<CustomTooltip />}
                                    cursor={<CustomCursor points={[]} />}
                                    position={{ y: 0 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="y"
                                    stroke={themes.incred.primary}
                                    strokeWidth={1.5}
                                    dot={false}
                                    activeDot={{ r: 8 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </section>

            <div className="flex justify-center gap-1 pb-4 pt-6 sm:py-3 md:gap-2">
                {periodMappings.map(({ key, label }) => (
                    <button
                        key={key}
                        className={`mx-sm-3 text-nowrap rounded-round py-[6px] sm:mx-1 ${period === key ? "active bg-orange-100" : ""} px-3 sm:px-4`}
                        onClick={() => handlePeriodChange(key as PeriodType)}
                    >
                        {label}
                    </button>
                ))}
            </div>
        </>
    ) : (
        <SkeletonAssetInfo />
    );

    if (hideTitle) return chartContent;
    return (
        <div
            className={`w-full rounded-3xl bg-white ${true ? "" : "sm:border sm:border-gray-200 sm:p-6"}`} /* sm:w-[830px] */
        >
            {chartContent}
        </div>
    );
};

interface AssetInformationProps {
    product: BULLION_KEY;
    /** When true (default), render the h1 title inside. Set false when title is rendered by parent for SEO. */
    showTitle?: boolean;
}

export default function AssetInformation({ product, showTitle = true }: AssetInformationProps) {
    return (
        <div className="w-full rounded-3xl bg-white">
            {showTitle && <AssetInformationTitle product={product} />}
            <Viz product={product} hideTitle />
        </div>
    );
}
